import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ArrowsClockwise, MagnifyingGlass } from '@phosphor-icons/react'
import { Input, Select } from '@/ui'
import { errorMessage } from '../../api/core'
import {
  useDispatch,
  useDispatchCorrection,
  useDispatches,
  usePackLot,
  type Dispatch,
  type DispatchItem,
} from '../../api/pack'
import { ActionButton } from '../../components/Can'
import { QueryState } from '../../components/Layout'
import {
  BoardColumns,
  BoardPage,
  BoardTable,
  FactRow,
  Facts,
  LinkButton,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  TwoLine,
  type BoardColumn,
} from '../../components/board'
import { FormDialog } from '../../components/FormDialog'
import { useUrlState } from '../../lib/useUrlState'
import { boardDate, boardWhen } from '../../model/payoutBoard'
import { packLotState, tripPouches } from '../../model/packing'
import { BoardCard, BoardField } from './shared'

/** Dispatches: every trip of released pouches to a customer; a row opens its record (V1-F7-08k). */
export function DispatchesPage() {
  const [f, setF] = useUrlState({ search: '', page: 1 })
  const list = useDispatches({ search: f.search || undefined, page: f.page })
  const navigate = useNavigate()
  const columns: BoardColumn<Dispatch>[] = [
    {
      id: 'inv',
      header: 'Invoice',
      cell: (d) => <TwoLine top={d.challanNo ?? d.code} sub={d.code} />,
    },
    { id: 'to', header: 'Customer', cell: (d) => d.toOrg ?? '—' },
    { id: 'lots', header: 'Lots', cell: (d) => d.items.map((i) => i.code).join(', ') || '—' },
    { id: 'pouches', header: 'Pouches', align: 'right', cell: (d) => tripPouches(d.items) },
    { id: 'when', header: 'Dispatched', hideBelow: 'sm', cell: (d) => boardWhen(d.dispatchedAt) },
    { id: 'from', header: 'From', hideBelow: 'md', cell: (d) => d.fromLocation ?? '—' },
  ]
  return (
    <BoardPage title="Dispatches">
      <div className="flex items-center gap-2">
        <MagnifyingGlass aria-hidden="true" className="text-muted" />
        <Input
          aria-label="Search invoice or trip"
          placeholder="Invoice or trip number"
          value={f.search}
          onChange={(e) => setF({ search: e.target.value })}
          className="h-9 max-w-[260px]"
        />
      </div>
      <BoardTable<Dispatch>
        caption="Dispatches"
        columns={columns}
        rows={list.data?.rows ?? []}
        getRowId={(d) => d.id}
        onRowClick={(d) => navigate(`/c/packing/dispatches/${d.id}`)}
        loading={list.isLoading}
        error={list.error ? errorMessage(list.error) : undefined}
        empty="No dispatches yet. The hall keeper dispatches released lots from the phone."
        pager={
          list.data
            ? {
                page: f.page,
                total: list.data.total,
                limit: list.data.limit,
                onPage: (page) => setF({ page }),
              }
            : undefined
        }
      />
      <Note>
        If a lot is held or recalled later, these records are the list of customers to call (F9).
      </Note>
    </BoardPage>
  )
}

/**
 * Dispatch · <invoice> (alignment map V1-F7-08k DispatchRecord; F7.8): customer, invoice, the lots
 * picked from the released list with their pouches and state. V1 has no vehicle, driver or bed
 * check. Corrections after dispatch are added with a reason; the record stays.
 */
export function DispatchRecordPage() {
  const { id } = useParams()
  const q = useDispatch(id)
  return (
    <QueryState query={q} label="the dispatch">
      {q.data && <DispatchRecord d={q.data} />}
    </QueryState>
  )
}

function DispatchRecord({ d }: { d: Dispatch }) {
  const [correcting, setCorrecting] = useState(false)
  const total = tripPouches(d.items)
  const lots = [...new Set(d.items.map((i) => i.code))]
  const columns: BoardColumn<DispatchItem>[] = [
    {
      id: 'lot',
      header: 'Pack lot',
      cell: (i) => <TwoLine top={i.code} sub={i.skuName ?? undefined} />,
    },
    { id: 'pouches', header: 'Pouches', align: 'right', cell: (i) => i.pouches },
    { id: 'state', header: 'State', cell: (i) => <LotState id={i.packLotId} /> },
    { id: 'loaded', header: 'Loaded', hideBelow: 'sm', cell: (i) => boardWhen(i.loadedAt) },
  ]
  return (
    <BoardPage
      title={`Dispatch · ${d.challanNo ?? d.code}`}
      actions={
        <LinkButton to="/c/packing/dispatches" icon={<ArrowLeft aria-hidden="true" />}>
          All dispatches
        </LinkButton>
      }
    >
      <Pill tone="ok">Dispatched from {d.fromLocation ?? 'the hall'}</Pill>
      <BoardColumns
        main={
          <>
            <BoardCard>
              <Facts>
                <FactRow label="Dispatch">{boardWhen(d.dispatchedAt)}</FactRow>
                <FactRow label="Customer">{d.toOrg ?? '—'}</FactRow>
                <FactRow label="Invoice" strong>
                  {d.challanNo ?? '—'} · {total} pouches
                </FactRow>
                <FactRow label="Trip">{d.code}</FactRow>
                <FactRow label="Lots">{lots.length} · picked from the released list</FactRow>
              </Facts>
            </BoardCard>
            <BoardTable<DispatchItem>
              caption="Lots on this trip"
              columns={columns}
              rows={d.items}
              getRowId={(i) => i.itemId}
              empty="No lots on this trip."
            />
            <Note>
              Only released lots not on hold were offered, each with at most the pouches still at
              the hall. Corrections after dispatch are added with a reason; this record stays.
            </Note>
          </>
        }
        side={
          <SidePanel label="Office actions" title="Office actions">
            <Note>
              If a lot is held or recalled later, these records are the list of customers to call
              (F9).
            </Note>
            <div className="flex-1" />
            <PanelActions>
              <ActionButton
                perm="pack-lots:dispatch"
                variant="secondary"
                leadingIcon={<ArrowsClockwise />}
                onClick={() => setCorrecting(true)}
              >
                Record a correction
              </ActionButton>
              {lots[0] && (
                <LinkButton
                  to={`/c/quality/recall?code=${encodeURIComponent(lots[0])}`}
                  icon={<MagnifyingGlass aria-hidden="true" />}
                >
                  Mock recall
                </LinkButton>
              )}
            </PanelActions>
          </SidePanel>
        }
      />
      <CorrectionDialog d={d} open={correcting} onClose={() => setCorrecting(false)} />
    </BoardPage>
  )
}

function LotState({ id }: { id: string }) {
  const q = usePackLot(id)
  if (!q.data) return <span className="text-muted">…</span>
  const s = packLotState(q.data.status, q.data.hold)
  const rel = q.data.decisions.find((x) => x.decision === 'RELEASE')
  return (
    <Pill tone={s.tone}>
      {s.text}
      {q.data.status === 'RELEASED' && rel?.decidedAt ? ` ${boardDate(rel.decidedAt)}` : ''}
    </Pill>
  )
}

function CorrectionDialog({
  d,
  open,
  onClose,
}: {
  d: Dispatch
  open: boolean
  onClose: () => void
}) {
  const correct = useDispatchCorrection()
  const lots = [...new Set(d.items.map((i) => i.code))]
  const [lot, setLot] = useState('')
  const [pouches, setPouches] = useState('')
  const [reason, setReason] = useState('')
  const lotCode = lot || lots[0] || ''
  const n = Number(pouches)
  const ok = lotCode && Number.isInteger(n) && n > 0 && reason.trim().length >= 3
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Record a correction"
      description="Pouches loaded but not recorded. A correction line is added; the trip is never edited."
      saveLabel="Add correction"
      loading={correct.isPending}
      canSave={Boolean(ok)}
      onSave={() =>
        correct.mutateAsync({ id: d.id, packLot: lotCode, pouches: n, reason: reason.trim() })
      }
    >
      <BoardField label="Pack lot">
        <Select
          value={lotCode}
          onChange={setLot}
          options={lots.map((c) => ({ value: c, label: c }))}
        />
      </BoardField>
      <BoardField label="Pouches">
        <Input
          inputMode="numeric"
          value={pouches}
          onChange={(e) => setPouches(e.target.value.replace(/\D/g, ''))}
        />
      </BoardField>
      <BoardField label="Reason">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
      </BoardField>
    </FormDialog>
  )
}
